import { inject, injectable } from 'inversify';
import { TYPES } from '@Types';
import { RequestAction, RequestFailure } from '../RequestAction';
import { Nullable, Map } from '@Models';
import { RequestMapsSuccess, RequestMapSuccess } from './response';
import { ApiClient, map_full } from '@Api';
import { MapMapper } from '@Mapper/ninthAge/map.mapper';
import { Locale } from '@Repository/locale';
import { CACHE_NAME } from '@Worker/CacheManager';

@injectable()
export class MapRepository {
  @inject(TYPES.Mapper.MapMapper) private _mapMapper!: MapMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;
  @inject(TYPES.Locale) private _locale!: Locale;

  public async getAll({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      const dto = await this._apiClient.ninthAge.getAllMaps({
        locale: locale ?? this._locale.getlocale(),
      });
      this.setCachedEntities(dto, locale ?? this._locale.getlocale());
      return new RequestMapsSuccess({
        payload: dto.map((item) => this._mapMapper.map(item)),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  public async get(
    entityId: number,
    {
      locale,
    }: {
      locale: Nullable<string>;
    },
  ): Promise<RequestAction> {
    try {
      const dto = await this._apiClient.ninthAge.getMap({
        mapId: entityId,
        locale: locale ?? this._locale.getlocale(),
      });
      this.setCachedEntity(dto, locale ?? this._locale.getlocale());
      return new RequestMapSuccess({
        payload: this._mapMapper.map(dto),
      });
    } catch (error: any) {
      console.log('error', error);
      return new RequestFailure({ error: error });
    }
  }

  public async getAllCached({
    locale,
  }: {
    locale: Nullable<string>;
  }): Promise<Nullable<Map[]>> {
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(
        `/${locale ?? this._locale.getlocale()}/ninth_age/maps`,
      );
      if (!response) return await null;

      const json: map_full[] = JSON.parse(await response.text());

      return json.map((item) => this._mapMapper.map(item)) ?? (await null);
    } catch (err) {
      console.log('error-load-cache', err);
    }
    return null;
  }

  public async getCached(
    entityId: number,
    {
      locale,
    }: {
      locale: string | null;
    },
  ): Promise<Map | null> {
    try {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(
        `/${locale ?? this._locale.getlocale()}/ninth_age/maps/${entityId}`,
      );
      if (!response) return await null;

      const json: map_full = JSON.parse(await response.text());

      return this._mapMapper.map(json);
    } catch (err) {
      console.log('error-load-cache', err);
    }
    return null;
  }

  protected async setCachedEntities(
    data: map_full[],
    locale: string,
  ): Promise<void> {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(
        `/${locale}/ninth_age/maps`,
        new Response(JSON.stringify(data)),
      );
    } catch (err) {
      console.log('error-save-cached-entity', err);
    }
  }

  protected async setCachedEntity(
    data: map_full,
    locale: string,
  ): Promise<void> {
    try {
      const cache = await caches.open(CACHE_NAME);
      await cache.put(
        `/${locale}/ninth_age/maps/${data.id}`,
        new Response(JSON.stringify(data)),
      );
    } catch (err) {
      console.log('error-save-cached-entity', err);
    }
  }
}
